import apiClient from "../../core/apiClient";
import { DraftFormRequest, FormValue, ItemFormDraft, TaskForm, TaskFormStatus } from "../../interfaces/DTO/forms/taskForm";
import { SimulationMimshakData } from "../../interfaces/DTO/simulation";

// Forms engine - a producer's form on a task (the agent's side): /task-forms/{taskId}
class TaskFormService {
  private url = (taskId: number) => `/task-forms/${taskId}`;

  /** The company's tasks that have a form, with its status */
  async statuses(): Promise<TaskFormStatus[]> {
    return (await apiClient.get<TaskFormStatus[]>("/task-forms/statuses")).data;
  }

  async get(taskId: number): Promise<TaskForm> {
    return (await apiClient.get<TaskForm>(this.url(taskId))).data;
  }

  /** Makes the task's form from the matching template, pre-filled from the data (the existing one if any) */
  async create(taskId: number): Promise<TaskForm> {
    return (await apiClient.post<TaskForm>(this.url(taskId))).data;
  }

  async updateValues(taskId: number, values: Record<string, FormValue>): Promise<TaskForm> {
    return (await apiClient.put<TaskForm>(`${this.url(taskId)}/values`, { values })).data;
  }

  async refresh(taskId: number): Promise<TaskForm> {
    return (await apiClient.post<TaskForm>(`${this.url(taskId)}/refresh`)).data;
  }

  /** The logged-in licence holder confirms - their saved signature, or the one drawn now when none is saved */
  async confirmAgentDeclaration(taskId: number, signatureDataUrl?: string | null): Promise<TaskForm> {
    return (await apiClient.post<TaskForm>(`${this.url(taskId)}/agent-declaration`, {
      signature_data_url: signatureDataUrl ?? null,
    })).data;
  }

  async send(taskId: number): Promise<TaskForm> {
    return (await apiClient.post<TaskForm>(`${this.url(taskId)}/send`)).data;
  }

  async reopen(taskId: number): Promise<TaskForm> {
    return (await apiClient.post<TaskForm>(`${this.url(taskId)}/reopen`)).data;
  }

  // ---------- a form prepared before its task exists ----------

  /** The form a task like this would get, pre-filled (available=false: no template) - nothing is stored */
  async draft(request: DraftFormRequest): Promise<TaskForm> {
    return (await apiClient.post<TaskForm>("/task-forms/draft", request)).data;
  }

  /**
   * A basket's items, before they are created: the tasks they will become whose workflow prepares forms, each with
   * the form it would get (pre-filled from its item) - nothing is stored
   */
  async drafts(contactId: number, items: SimulationMimshakData[]): Promise<ItemFormDraft[]> {
    return (await apiClient.post<ItemFormDraft[]>("/task-forms/drafts", { contact_id: contactId, items })).data;
  }

  async checkDraft(versionId: number, values: Record<string, FormValue>): Promise<string[]> {
    return (await apiClient.post<{ problems: string[] }>("/task-forms/draft/check", { version_id: versionId, values })).data.problems;
  }

  /** A draft's page as the server renders it (what will be signed) - an object URL, revoke it when done */
  async draftPageUrl(versionId: number, values: Record<string, FormValue>, page: number, withAgentSignature = true): Promise<string> {
    const res = await apiClient.post<Blob>(`/task-forms/draft/pages/${page}`, {
      version_id: versionId, values, with_agent_signature: withAgentSignature,
    }, { responseType: "blob" });
    return URL.createObjectURL(res.data);
  }

  /** A published version's blank page (a draft's background) - an object URL, revoke it when done */
  async publishedTemplatePageUrl(versionId: number, page: number): Promise<string> {
    const res = await apiClient.get<Blob>(`/task-forms/template-versions/${versionId}/pages/${page}`, { responseType: "blob" });
    return URL.createObjectURL(res.data);
  }

  /** The prepared form saved on the task just created - and sent to the client for signature */
  async savePrepared(taskId: number, versionId: number, values: Record<string, FormValue>, agentSignatureDataUrl?: string | null): Promise<TaskForm> {
    return (await apiClient.post<TaskForm>(`${this.url(taskId)}/prepared`, {
      version_id: versionId, values, agent_signature_data_url: agentSignatureDataUrl ?? null,
    })).data;
  }

  /** The form the client signed (with its audit page) as an object URL - revoke it when done */
  /** The form as it is now (the saved values) as a PDF - a Blob to download */
  async formPdf(taskId: number): Promise<Blob> {
    const res = await apiClient.get<Blob>(`${this.url(taskId)}/pdf`, {
      responseType: "blob",
      params: { t: Date.now() },
    });
    return res.data;
  }

  async signedPdfUrl(taskId: number): Promise<string> {
    const res = await apiClient.get<Blob>(`${this.url(taskId)}/signed-pdf`, { responseType: "blob" });
    return URL.createObjectURL(res.data);
  }

  /** The blank template page (the background) as an object URL - revoke it when done */
  async templatePageUrl(taskId: number, versionId: number, page: number): Promise<string> {
    const res = await apiClient.get<Blob>(`${this.url(taskId)}/template-pages/${page}`, {
      params: { version_id: versionId },
      responseType: "blob",
    });
    return URL.createObjectURL(res.data);
  }

  /** A page as it will be signed (the server's render) as an object URL - revoke it when done */
  async renderedPageUrl(taskId: number, page: number): Promise<string> {
    const res = await apiClient.get<Blob>(`${this.url(taskId)}/pages/${page}`, {
      responseType: "blob",
      params: { t: Date.now() }, // never a cached render
    });
    return URL.createObjectURL(res.data);
  }
}

export const taskFormService = new TaskFormService();
